import { uniqueId } from 'lodash-es'
import type { MqttClient } from 'mqtt'

import { SubscriptionItem } from './subscriptionsBase'

// CONNACK/AUTH reason codes (MQTT v3 return codes and v5 reason codes) that mean the
// broker rejected the credentials themselves - retrying with the same token can never
// succeed, so these are treated as fatal rather than fed back into the retry loop.
const AUTH_FAILURE_REASON_CODES = new Set([4, 5, 134, 135])

const RECONNECT_BASE_DELAY_MS = 1000
const RECONNECT_MAX_DELAY_MS = 60000

export class FlespiListener {
  token: string | undefined
  host: string | undefined
  clientId: string | undefined
  sessionName: string | undefined
  mqttClient!: MqttClient
  protocolNumber: 5 | 4 | 3
  subscriptions: Array<SubscriptionItem>
  subscriptionsConfig: Record<string, any>

  /**
   * Invoked when the broker rejects the current token outright (bad credentials /
   * not authorized). Reconnecting with the same token is pointless, so the listener
   * stops retrying and lets the caller fetch a fresh token and open a new listener.
   */
  onAuthFailure?: (error: any) => void

  private reconnectTimer: ReturnType<typeof setTimeout> | undefined
  private reconnectDelayMs = RECONNECT_BASE_DELAY_MS
  private authFailed = false
  private ended = false
  private runId = 0

  constructor() {
    this.protocolNumber = 5
    this.subscriptions = []
    this.subscriptionsConfig = {}
  }

  init({
    token,
    host,
    clientPrefix,
    sessionName,
  }: {
    token: string
    host: string
    clientPrefix: string
    sessionName: string
  }) {
    this.token = token
    this.host = host
    this.sessionName = sessionName
    const clientIdNumber: number = Math.round(Math.random() * 1000000)
    this.clientId = `${clientPrefix}-${clientIdNumber}`
    console.debug('Flespi broker was initialized')
  }

  get connectionSettings() {
    return {
      username: this.token,
      clientId: this.clientId,
      protocolVersion: this.protocolNumber,
      clean: true,
      // Reconnects are scheduled manually with backoff (see scheduleReconnect) so a
      // fatal auth rejection can stop retrying instead of hammering flespi every second.
      reconnectPeriod: 0,
      resubscribe: true,
      queueQoSZero: false,
    }
  }

  async subscribe() {
    if (!this.subscriptions.length) {
      return
    }

    console.debug('Mqtt client started subscription')
    const events: Array<Promise<any>> = []
    this.subscriptions.forEach((subscriptionItem) => {
      try {
        const subscriptionIdentifier = parseInt(uniqueId())
        console.debug('Mqtt client subscription topic: ', subscriptionItem.topic)
        events.push(
          this.mqttClient.subscribeAsync(subscriptionItem.topic, {
            properties: { subscriptionIdentifier },
          }),
        )
        this.subscriptionsConfig[subscriptionIdentifier] = subscriptionItem
      } catch (e) {
        console.error(e)
      }
    })

    if (events.length) {
      await Promise.all(events)
    }
  }
  async onConnect() {
    console.debug('Mqtt client connected')
    this.reconnectDelayMs = RECONNECT_BASE_DELAY_MS
    try {
      await this.subscribe()
    } catch (error) {
      console.error('Error on trying to subscribe: ', error)
    }
  }

  async onDisconnect() {
    console.warn('Mqtt client disconnected')
  }

  async onError(error: any) {
    if (this.isAuthFailure(error)) {
      console.warn('Flespi rejected the current token, stopping reconnect attempts')
      this.authFailed = true
      this.clearReconnectTimer()
      this.onAuthFailure?.(error)
    } else {
      console.error('Mqtt client get error: ', error)
    }
  }

  async onClose() {
    if (this.ended || this.authFailed) {
      return
    }
    this.scheduleReconnect()
  }

  private isAuthFailure(error: any): boolean {
    return typeof error?.code === 'number' && AUTH_FAILURE_REASON_CODES.has(error.code)
  }

  private scheduleReconnect() {
    if (this.reconnectTimer) {
      return
    }
    const delay = this.reconnectDelayMs
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined
      this.mqttClient?.reconnect()
    }, delay)
    // Back off further in case this reconnect attempt also fails; reset happens on connect
    this.reconnectDelayMs = Math.min(delay * 2, RECONNECT_MAX_DELAY_MS)
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = undefined
    }
  }

  async onMessage(topic: string, message: any, packet: any) {
    let data = message.toString()
    if (data) {
      try {
        data = JSON.parse(data)
      } catch (error) {
        console.error('Error on trying to json parse message data: ', error, message.toString())
      }
    }
    const subscriptionIdentifier = packet.properties.subscriptionIdentifier
    const userProperties = packet.properties.userProperties

    if (this.subscriptionsConfig[subscriptionIdentifier]) {
      const subscription = this.subscriptionsConfig[subscriptionIdentifier]
      const topicData = subscription.getTopicData(topic)
      await this.subscriptionsConfig[subscriptionIdentifier].messageHandler({
        topic,
        data,
        topicData,
        userProperties,
      })
    }
  }

  run(subscriptions: Array<SubscriptionItem>) {
    if (!(this.token && this.host && this.sessionName && this.clientId)) {
      return
    }

    console.debug('Run flespi broker')
    this.subscriptions = subscriptions
    this.ended = false
    this.authFailed = false
    this.reconnectDelayMs = RECONNECT_BASE_DELAY_MS
    void this.connect(this.host, ++this.runId)
  }

  private async connect(host: string, runId: number) {
    const { default: mqtt } = await import('mqtt')
    // end() or a newer run() may have happened while mqtt was loading
    if (this.ended || runId !== this.runId) {
      return
    }

    this.mqttClient = mqtt.connect(host, this.connectionSettings)
    this.mqttClient.on('connect', this.onConnect.bind(this))
    this.mqttClient.on('disconnect', this.onDisconnect.bind(this))
    this.mqttClient.on('message', this.onMessage.bind(this))
    this.mqttClient.on('error', this.onError.bind(this))
    this.mqttClient.on('close', this.onClose.bind(this))
    console.debug('Flespi broker was started')
  }

  end() {
    this.ended = true
    this.clearReconnectTimer()
    if (this.mqttClient) {
      this.mqttClient.end()
    }
  }
}
